import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
    RowSelectionState,
    DropdownItem,
    Multiselect,
    M,
    Button,
} from 'design-system-web';
import { bulkAssignTaskTeamsMutation } from 'graphql-queries';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Assignable } from 'domains-work-order-type-contracts';

type TeamsSubmittingAction = 'assign' | 'unassign';

const TASK_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    assignableTeams: Assignable[];
    refetch?: () => void;
};

export function BulkTeamsAssignAction({ rowSelection, assignableTeams, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkAssignTaskTeams] = useMutation(bulkAssignTaskTeamsMutation);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTeams, setSelectedTeams] = useState<Assignable[]>([]);
    const [submittingAction, setSubmittingAction] =
        useState<TeamsSubmittingAction | null>(null);
    const isSubmitting = submittingAction !== null;

    const selectedTaskIds = useMemo(() => {
        if (!rowSelection) {
            return [];
        }

        return Object.entries(rowSelection)
            .filter(([, isSelected]) => isSelected)
            .map(([taskId]) => taskId);
    }, [rowSelection]);

    const openModal = async () => {
        if (selectedTaskIds.length === 0) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.noSelection')
            );

            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.limitExceeded', {
                    taskSelectionLimit: TASK_SELECTION_LIMIT,
                    selectedTaskCount: selectedTaskIds.length,
                }),
                5000
            );

            return;
        }

        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedTeams([]);
    };

    const submitBulkTeams = async (assigneeGroups: string[]) => {
        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.limitExceeded', {
                    taskSelectionLimit: TASK_SELECTION_LIMIT,
                    selectedTaskCount: selectedTaskIds.length,
                }),
                5000
            );

            closeModal();

            return;
        }

        setSubmittingAction(
            assigneeGroups.length === 0 ? 'unassign' : 'assign'
        );

        try {
            const result = await bulkAssignTaskTeams({
                variables: {
                    taskIds: selectedTaskIds,
                    assigneeGroups,
                },
            });

            const { successCount, failureCount } = result.data?.bulkAssignTaskTeams || {
                successCount: 0,
                failureCount: selectedTaskIds.length,
            };

            if (failureCount === 0) {
                if (assigneeGroups.length === 0) {
                    await window.Toast.show(
                        t(
                            'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.unassignAll.success',
                            {
                                count: successCount,
                            }
                        )
                    );
                } else {
                    await window.Toast.show(
                        t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.success', {
                            count: successCount,
                        })
                    );
                }
            } else if (successCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.error')
                );
            } else {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.partial', {
                        successCount,
                        failureCount,
                    }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.error')
            );
        }

        setSubmittingAction(null);
        closeModal();
    };

    const handleConfirm = async () => {
        if (selectedTeams.length === 0) {
            return;
        }

        await submitBulkTeams(selectedTeams.map(team => team.value));
    };

    const handleUnassignAll = async () => {
        await submitBulkTeams([]);
    };

    return (
        <>
            <Button
                variant="secondary"
                size="large"
                onClick={openModal}
                testId="bulk-task-teams-assign-select"
            >
                {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.selectTeams')}
            </Button>

            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                handleConfirm={handleConfirm}
                title={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.confirm.title')}
                confirmText={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.confirm.button')}
                cancelText={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.cancel.button')}
                loading={isSubmitting}
                confirmLoading={submittingAction === 'assign'}
                confirmActionDisabled={selectedTeams.length === 0 || isSubmitting}
                cancelActionDisabled={isSubmitting}
                isFullWidth
                footerActionsAfterConfirm={
                    <Button
                        variant="secondary"
                        size="large"
                        onClick={handleUnassignAll}
                        disabled={isSubmitting}
                        loading={submittingAction === 'unassign'}
                        testId="bulk-task-teams-unassign-all"
                    >
                        {t(
                            'web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.unassignAll.button'
                        )}
                    </Button>
                }
            >
                <M>
                    {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.modal.prompt', {
                        count: selectedTaskIds.length,
                    })}
                </M>
                <Multiselect
                    items={assignableTeams ?? []}
                    value={selectedTeams.map(team => ({ value: team.value }))}
                    onChange={(items: DropdownItem<string>[]) => {
                        const matchedTeams = items
                            .map(item =>
                                (assignableTeams ?? []).find(team => team.value === item.value)
                            )
                            .filter((team): team is Assignable => Boolean(team));

                        setSelectedTeams(matchedTeams);
                    }}
                    placeholder={t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.assignTeams.selectTeams')}
                    doTranslation={false}
                    isFullWidth
                />
            </ConfirmationModal>
        </>
    );
}
